/*
 * Split-variant PDP features.
 * Powers two behaviors on products tagged `split-variants`, gated per-instance
 * by data attributes rendered from the "Split-variant features" block:
 *   1. Per-variant description  — show the selected variant's rich-text
 *      metafield (custom.variant_description) and swap it on color change.
 *   2. Per-color gallery filter  — show only the gallery/thumbnail images whose
 *      ALT text starts with the selected color value (word-boundary match).
 *      Covers the inline gallery + thumbnails AND the zoom/fullscreen dialog.
 *
 * Zoom dialog navigation: Blum's fullscreen gallery caches ALL slides at init
 * and navigates by index over the full set (so hiding a slide with display:none
 * would leave prev/next landing on a blank + a wrong "X / Y"). For split
 * products we therefore intercept the dialog's prev/next in the capture phase
 * and drive navigation over just the selected color's slides ourselves.
 *
 * Data comes from <variant-split> elements written by
 * snippets/variant-split-features.liquid. All Liquid-generated values live on
 * that element / its JSON script; the logic below is static and cacheable.
 */
(function () {
  'use strict';

  // Inline gallery containers (live in the main product section).
  var MAIN_CONTAINERS = [
    { sel: '.product-slideshow__items', counter: 'main' }, // main gallery slides
    { sel: '.thumbnail__horizoltal-carousel', counter: null }, // horizontal thumbs (theme's own spelling)
    { sel: '.product-slideshow__thumbs', counter: null } // vertical thumbs
  ];

  // Zoom/fullscreen dialog containers. Classes are unique to
  // snippets/main-product-gallery-dialog.liquid. The dialog's own counter is
  // reconciled separately (refreshDialog), so these carry no `counter`.
  var DIALOG_CONTAINERS = [
    { sel: '.dialog-gallery-slides', counter: null }, // fullscreen slides
    { sel: '.horizontal-carousel', counter: null } // fullscreen thumbs
  ];

  var MEDIA_SELECTOR = 'img, video, model-viewer, iframe';

  var updaters = [];

  function norm(s) {
    return (s || '').toString().trim().toLowerCase();
  }

  // Word-boundary match so "pink" does not match "pink shimmer".
  function matchesColor(alt, color) {
    if (!color || !alt) return false;
    if (alt === color) return true;
    if (alt.indexOf(color) !== 0) return false;
    var next = alt.charAt(color.length);
    return /[^a-z0-9]/.test(next);
  }

  function initSplit(root) {
    var sectionId = root.getAttribute('data-section-id');
    var section = document.getElementById(sectionId) || document;
    var selectedId = root.getAttribute('data-selected');
    var showDescription = root.getAttribute('data-show-description') === 'true';
    var showGallery = root.getAttribute('data-show-gallery-filter') === 'true';
    var dialogRoot = null;

    var colors = {};
    var colorsEl = root.querySelector('.variant-split__colors');
    if (colorsEl) {
      try {
        colors = JSON.parse(colorsEl.textContent);
      } catch (e) {
        colors = {};
      }
    }

    function currentVariantId() {
      var v = new URL(window.location.href).searchParams.get('variant');
      if (v) return v;
      var input = section.querySelector('[name="id"]') || document.querySelector('form [name="id"]');
      if (input && input.value) return input.value;
      return String(selectedId);
    }

    function currentColor() {
      return colors[String(currentVariantId())] || '';
    }

    function updateDescription(id) {
      root.querySelectorAll('.variant-description__item').forEach(function (el) {
        el.hidden = el.getAttribute('data-variant-id') !== id;
      });
    }

    // ---- Inline gallery + thumbnail filtering -----------------------------

    // Reconcile the inline gallery's "X / Y" counter to the filtered count.
    // Blum owns it but recomputes to the same visible count because hidden
    // items have zero width, so the two agree.
    function updateMainCounter(visible) {
      var total = section.querySelector('.js-carousel-counter-total');
      var current = section.querySelector('.js-carousel-counter-current');
      if (total) total.textContent = visible;
      if (current) current.textContent = '1';
    }

    function filterContainer(container, color, counter) {
      var items = Array.prototype.slice.call(container.children);
      var anyMatch = items.some(function (it) {
        var img = it.querySelector && it.querySelector('img');
        return img && matchesColor(norm(img.getAttribute('alt')), color);
      });
      var visible = 0;
      items.forEach(function (it) {
        var img = it.querySelector && it.querySelector('img');
        var alt = img ? norm(img.getAttribute('alt')) : '';
        // Non-image media (video/3D) and the no-match fallback stay visible.
        var show = !img || !anyMatch || matchesColor(alt, color);
        it.style.display = show ? '' : 'none';
        // Count only shown media slides — never pagination/controls that share
        // the container (e.g. the dialog's .slide__pagination child).
        if (show && it.querySelector && it.querySelector(MEDIA_SELECTOR)) visible++;
      });
      container.scrollLeft = 0;
      container.scrollTop = 0;
      if (counter === 'main') updateMainCounter(visible);
    }

    // ---- Zoom dialog navigation takeover ----------------------------------

    function dialogSlides() {
      return dialogRoot ? Array.prototype.slice.call(dialogRoot.querySelectorAll('.js-gallery-slide')) : [];
    }

    // Slides whose image matches the selected color. No-match falls back to the
    // full set (so a color with zero tagged images never blanks the dialog).
    function matchingSlides(slides, color) {
      if (!color) return slides;
      var m = slides.filter(function (s) {
        var img = s.querySelector('img');
        return img && matchesColor(norm(img.getAttribute('alt')), color);
      });
      return m.length ? m : slides;
    }

    // Mirror Blum's active-slide swap so zoom/behavior stay consistent.
    function activateSlide(slides, target) {
      slides.forEach(function (s) {
        var zoom = s.querySelector('sht-zoom');
        if (s === target) {
          s.classList.remove('d-none');
          s.classList.add('is-active-gallery-slide', 'd-flex');
          if (zoom && zoom.init) zoom.init();
        } else {
          s.classList.remove('is-active-gallery-slide', 'd-flex');
          s.classList.add('d-none');
          if (zoom && zoom.reset) zoom.reset();
        }
      });
    }

    function setDialogCounter(current, total) {
      if (!dialogRoot) return;
      var currentEl = dialogRoot.querySelector('.js-slide-current');
      var totalEl = dialogRoot.querySelector('.js-slide-total');
      if (currentEl) currentEl.textContent = current;
      if (totalEl) totalEl.textContent = total;
      var prev = dialogRoot.querySelector('.js-slide-prev-btn');
      var next = dialogRoot.querySelector('.js-slide-next-btn');
      if (prev) current <= 1 ? prev.setAttribute('disabled', 'disabled') : prev.removeAttribute('disabled');
      if (next) current >= total ? next.setAttribute('disabled', 'disabled') : next.removeAttribute('disabled');
    }

    // Ensure the active slide belongs to the selected color and the counter is
    // color-relative. Called on open and whenever the color changes.
    function refreshDialog() {
      if (!showGallery || !dialogRoot) return;
      var slides = dialogSlides();
      if (!slides.length) return;
      var matching = matchingSlides(slides, currentColor());
      if (!matching.length) return;
      var active = dialogRoot.querySelector('.js-gallery-slide.is-active-gallery-slide');
      var pos = matching.indexOf(active);
      if (pos < 0) {
        activateSlide(slides, matching[0]);
        pos = 0;
      }
      setDialogCounter(pos + 1, matching.length);
    }

    function navigateDialog(dir) {
      var slides = dialogSlides();
      var matching = matchingSlides(slides, currentColor());
      if (!matching.length) return;
      var active = dialogRoot.querySelector('.js-gallery-slide.is-active-gallery-slide');
      var pos = matching.indexOf(active);
      if (pos < 0) pos = 0;
      pos = Math.max(0, Math.min(matching.length - 1, pos + dir));
      activateSlide(slides, matching[pos]);
      setDialogCounter(pos + 1, matching.length);
    }

    function setupDialogTakeover() {
      if (!dialogRoot || dialogRoot.getAttribute('data-vs-bound')) return;
      dialogRoot.setAttribute('data-vs-bound', '1');

      // Capture phase → runs before the event reaches Blum's button listener,
      // so its index-based handler never fires while a color filter narrows the
      // slide set. When nothing is filtered, defer to Blum.
      dialogRoot.addEventListener(
        'click',
        function (e) {
          var next = e.target.closest && e.target.closest('.js-slide-next-btn');
          var prev = e.target.closest && e.target.closest('.js-slide-prev-btn');
          if (!next && !prev) return;
          var slides = dialogSlides();
          var matching = matchingSlides(slides, currentColor());
          if (matching.length >= slides.length) return; // not filtered → Blum handles
          e.stopImmediatePropagation();
          e.preventDefault();
          navigateDialog(next ? 1 : -1);
        },
        true
      );

      // Thumbnail clicks are handled by Blum (by media id); resync the counter.
      dialogRoot.addEventListener('click', function (e) {
        if (!(e.target.closest && e.target.closest('.js-gallery-thumb, .js-gallery-btn-thumb'))) return;
        setTimeout(refreshDialog, 0);
      });
    }

    // ---- Orchestration ----------------------------------------------------

    function updateGallery() {
      var color = currentColor();
      MAIN_CONTAINERS.forEach(function (c) {
        section.querySelectorAll(c.sel).forEach(function (el) {
          filterContainer(el, color, c.counter);
        });
      });
      DIALOG_CONTAINERS.forEach(function (c) {
        document.querySelectorAll(c.sel).forEach(function (el) {
          filterContainer(el, color, c.counter);
        });
      });
      refreshDialog();
    }

    function update() {
      var id = String(currentVariantId());
      if (showDescription) updateDescription(id);
      if (showGallery) updateGallery();
    }

    if (showGallery) {
      // Re-apply when Blum re-renders the inline gallery (childList changes).
      MAIN_CONTAINERS.forEach(function (c) {
        section.querySelectorAll(c.sel).forEach(function (el) {
          new MutationObserver(function () {
            setTimeout(update, 0);
          }).observe(el, { childList: true });
        });
      });

      // The zoom dialog is cloned from a <template> only when opened, so its
      // containers don't exist at init. Watch the dialog for that insertion,
      // then filter + take over navigation.
      var tpl = section.querySelector('#productGallery-' + sectionId) || document.getElementById('productGallery-' + sectionId);
      dialogRoot = tpl && tpl.closest('sht-dialog');
      if (dialogRoot) {
        setupDialogTakeover();
        new MutationObserver(function () {
          setTimeout(function () {
            update();
            refreshDialog();
          }, 40);
        }).observe(dialogRoot, { childList: true, subtree: true });
      }
    }

    updaters.push(update);
    update();
  }

  function runAll() {
    updaters.forEach(function (u) {
      u();
    });
  }

  function boot() {
    var roots = document.querySelectorAll('variant-split');
    if (!roots.length) return;
    roots.forEach(initSplit);

    // Variant-change signals, hooked once for all instances. Blum's variant
    // picker calls history.replaceState + dispatches a form `change` event.
    ['pushState', 'replaceState'].forEach(function (m) {
      var orig = history[m];
      history[m] = function () {
        var r = orig.apply(this, arguments);
        setTimeout(runAll, 30);
        return r;
      };
    });
    window.addEventListener('popstate', function () {
      setTimeout(runAll, 30);
    });
    document.addEventListener('change', function (e) {
      if (e.target && e.target.closest('form')) setTimeout(runAll, 60);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
